import { updateKeyInStore } from 'App';
import { store } from 'app/redux/store';
import { countries } from 'countries';
import * as Yup from 'yup';
import { requiredFiles } from '../../useValidateQuestionnaire';
import {
  TDidYourEmployerProvideAnAllowanceOnTopOfDirectSalaryListRow,
  TForeignEmployer,
  TForeignEmployerRow,
  updateFieldArrayKey,
  updateKey,
  WagesStateValues,
} from './slice';

// predicate:
//       'Displayed in case the user selects "Yes" to the Wages question in the General tab.',

export const fields: IBaseField<
  | keyof WagesStateValues
  | keyof TForeignEmployerRow
  | keyof TDidYourEmployerProvideAnAllowanceOnTopOfDirectSalaryListRow
>[] = [
  {
    dependenciesTitle: 'Foreign (i.e Non-US) Sourced Income',
    name: 'incomeOutsideOfTheUs',
    label: 'Income Outside of the US',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'didYouEarnWagesFromANonUsEmployer',
        label: 'Did you earn wages from a non-US Employer?',
        type: 'radio',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),

        dependencies: [
          {
            name: 'foreignEmployer',
            label: 'foreignEmployer',
            type: 'fieldArray',
            predicate: (value) => value === 'yes',
            tooltip: '',
            comment: true,
            effect: (payload) => store.dispatch(updateFieldArrayKey(payload)),
            defaultDependency: {
              foreignIeNonUSEmployerName: null,
              isEmployeraForeignCompanyaForeignSubsidiaryOfAUSCompanyOrAUSCompany:
                null,
              employersStreetAddress: null,
              City: null,
              PostCode: null,
              Country: null,
              countryGrossWagessalaryEarnedWithThisEmployerDuringYear: null,
              didYourEmployerProvideAnAllowanceOnTopOfDirectSalary: null,
              didYourEmployerProvideAnAllowanceOnTopOfDirectSalaryList: null,
              wereContributionsMadeToaNonUSPensionPlan: null,
              amountYourEmployerContributed: null,
            },
            dependencies: [
              {
                name: 'foreignIeNonUSEmployerName',
                label: 'Foreign (i.e Non-US) employer name',
                type: 'text',
                tooltip: '',
                comment: true,
                effect: updateKeyInStore(updateKey),
                placeholder: 'Enter',
              },
              {
                name: 'isEmployeraForeignCompanyaForeignSubsidiaryOfAUSCompanyOrAUSCompany',
                label:
                  'Is Employer a foreign company, a foreign subsidiary of a U.S. company, or a U.S. company?',
                type: 'select',
                options: [
                  { label: 'Foreign Company', value: 'Foreign Company' },
                  {
                    label: 'Foreign Subsidary of U.S. Company',
                    value: 'Foreign Subsidary of U.S. Company',
                  },
                  { label: 'U.S. Company', value: 'U.S. Company' },
                  {
                    label: 'International Organization',
                    value: 'International Organization',
                  },
                  { label: 'Other', value: 'Other' },
                ],
                tooltip: '',
                comment: true,
                effect: updateKeyInStore(updateKey),
              },
              {
                name: 'employersStreetAddress',
                label: "Employer's street address",
                type: 'text',
                tooltip: '',
                comment: true,
                effect: updateKeyInStore(updateKey),
                placeholder: 'Enter',
              },
              {
                name: 'City',
                label: 'City',
                type: 'text',
                tooltip: '',
                comment: true,
                effect: updateKeyInStore(updateKey),
                placeholder: 'Enter',
              },
              {
                name: 'PostCode',
                label: 'Post Code',
                type: 'text',
                tooltip: '',
                comment: true,
                effect: updateKeyInStore(updateKey),
                placeholder: 'Enter',
              },

              {
                name: 'Country',
                label: 'Country',
                type: 'country',
                tooltip: '',
                comment: true,
                effect: updateKeyInStore(updateKey),
              },
              {
                name: 'countryGrossWagessalaryEarnedWithThisEmployerDuringYear',
                label:
                  'Gross wages/salary earned with this employer during {year}?',
                type: 'currency',
                tooltip: '',
                comment: true,
                effect: updateKeyInStore(updateKey),
              },
              {
                name: 'didYourEmployerProvideAnAllowanceOnTopOfDirectSalary',
                label:
                  'Did your employer provide an allowance on top of direct salary (car, education, home leave, etc)',
                type: 'radio',
                tooltip: '',
                comment: true,
                effect: updateKeyInStore(updateKey),
                dependencies: [
                  {
                    name: 'didYourEmployerProvideAnAllowanceOnTopOfDirectSalaryList',
                    label: 'List',
                    type: 'fieldArray',
                    predicate: (value) => value === 'yes',
                    tooltip: '',
                    effect: (payload) =>
                      store.dispatch(updateFieldArrayKey(payload)),
                    comment: true,
                    defaultDependency: {
                      descriptionOfAllowance: null,
                      amount: null,
                    },
                    dependencies: [
                      {
                        name: 'descriptionOfAllowance',
                        label: 'Description of Allowance',
                        type: 'text',
                        tooltip: '',
                        comment: true,
                        predicate: (value) => value === 'yes',
                        effect: updateKeyInStore(updateKey),
                        placeholder: 'Enter',
                      },
                      {
                        name: 'amount',
                        label: 'Amount',
                        type: 'currency',
                        tooltip: '',
                        comment: true,
                        predicate: (value) => value === 'yes',

                        effect: updateKeyInStore(updateKey),
                      },
                    ],
                  },
                ],
              },
              {
                name: 'wereContributionsMadeToaNonUSPensionPlan',
                label:
                  'Were contributions made to a non-US pension plan (whether yourself or by your employer)?',
                subLabel: () => (
                  <p>
                    Such as Pillar II, Superannuation, KiwiSaver, Employer
                    Matching Contribution, etc
                  </p>
                ),
                type: 'radio',
                tooltip: '',
                comment: true,
                effect: updateKeyInStore(updateKey),
                dependencies: [
                  {
                    name: 'amountYourEmployerContributed',
                    label: 'Amount Your Employer Contributed',
                    type: 'currency',
                    predicate: (value) => value === 'yes',
                    tooltip: '',
                    comment: true,
                    effect: updateKeyInStore(updateKey),
                  },
                ],
              },
            ],
          },

          {
            name: 'didYouEarnWagesFromAUsEmployer',
            label: 'Did you earn wages from a US Employer?',
            type: 'radio',
            predicate: (value) => value === 'no',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
            dependencies: [
              {
                name: 'pleaseUploadFormW2',
                label: 'Please upload Form W2',
                type: 'upload',
                predicate: (value) => value === 'yes',
                tooltip: '',
                comment: true,
                effect: updateKeyInStore(updateKey),
              },
            ],
          },
        ],
      },
      {
        name: 'uploadAnnualNonUsWageStatementFromForeignEmployers',
        label: 'Upload annual non-US wage statement from foreign employers',
        subLabel: () => <p>T4, Certificat de Salaire, PAYE, etc.</p>,
        type: 'upload',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
      {
        name: 'didYouReceiveAForeignieNonUsTaxRefund',
        label: 'Did you  receive a foreign (ie non-US) tax refund?',
        type: 'radio',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        dependencies: [
          {
            name: 'amountOfForeignieNonUsTaxRefund',
            label: 'Amount of foreign (ie Non-US) tax refund',
            type: 'currency',
            predicate: (value) => value === 'yes',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
          },
        ],
      },
      {
        name: 'didYouReceiveStockBasedCompensationFromYourEmployer',
        label: 'Did you receive stock-based compensation from your employer?',
        type: 'upload',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        dependencies: [
          {
            name: 'pleaseUploadSupportingDocumentation',
            label: 'Please upload supporting documentation',
            type: 'upload',
            predicate: (value) => value === 'yes',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
          },
        ],
      },
    ],
  },
  {
    dependenciesTitle: 'US Sourced Income',
    name: 'incomeFromTheUs',
    label: 'Income from the US',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'didYouReceiveWagesDuringTheYearFromAUsEmployer',
        label: 'Did you receive wages during the year from a US employer?',
        type: 'radio',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        dependencies: [
          {
            name: 'pleaseUploadFormW2',
            label: 'Please upload Form W-2',
            type: 'upload',
            predicate: (value) => value === 'yes',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
          },
          {
            name: 'areYouAUsGovernmentEmployee',
            label: 'Are you a US government employee?',
            type: 'upload',
            predicate: (value) => value === 'yes',
            tooltip: '',
            comment: true,
            effect: updateKeyInStore(updateKey),
          },
        ],
      },
    ],
  },
  {
    name: 'didYouPerformWorkAsAConsultantOrIndependentContractorForAUsFirm',
    label:
      'Did you perform work as a consultant or independent contractor for a US firm?',
    type: 'upload',
    predicate: (value) => value === 'yes',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'pleaseUploadForm1099Misc',
        label: 'Please upload Form 1099-MISC',
        type: 'upload',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
    ],
  },

  {
    name: 'didYouReceiveRoyaltiesUnemploymentPaymentsOrOtherMiscellaneousIncomeFromAUsSource',
    label:
      'Did you receive royalties, unemployment payments or other miscellaneous income from a US source?',
    type: 'upload',
    predicate: (value) => value === 'yes',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'pleaseUploadACopyOfForm1099MiscOrFormK1',
        label: 'Please upload a copy of form 1099-MISC or Form K-1',
        type: 'upload',
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
    ],
  },
];

export const validationSchema = Yup.object().shape({});
